import * as React from 'react';

import type { Duration } from 'moment';
import moment from 'moment';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Typography } from '@coursera/cds-core';
import { color } from '@coursera/coursera-ui';
import { SvgCheckSolid, SvgUserFilled } from '@coursera/coursera-ui/svg';

import withSvgStack from 'bundles/coursera-ui/components/hocs/withSvgStack';

import _t from 'i18n!nls/accomplishments';

import 'css!bundles/accomplishments/components/shared/__styles__/CompletionOverview';

const SvgUserFilledStack = withSvgStack(SvgUserFilled);

const DEFAULT_AVATAR_SIZE = 95;
const DEFAULT_AVATAR_RATIO = 0.6;

type Props = {
  name: string;
  userName: string;
  userPhoto?: string;
  grade?: string;
  completedCourseNames?: Array<string>;
  isS12n?: boolean;
  productLink: string;
  completionDate: string;
  partnerNames?: string;
  includeGrade?: boolean;
  totalContentWeeks?: number | null;
  totalDuration?: Duration | null;
  totalLearningMonths?: number | null;
  totalLearningHoursPerWeek?: number | null;
};

const CompletionOverview: React.FC<Props> = ({
  name,
  userName,
  userPhoto,
  grade,
  completedCourseNames,
  productLink,
  completionDate,
  partnerNames,
  includeGrade,
  totalContentWeeks,
  isS12n,
  totalDuration,
  totalLearningMonths,
  totalLearningHoursPerWeek,
}: Props) => {
  const s12nCompletionMessage = (
    <FormattedMessage
      message={_t(
        "{userName}'s account is verified. Coursera certifies their successful completion of {partnerNames} {productLink} Specialization."
      )}
      userName={userName}
      productLink={
        <Link className="product-link" href={productLink}>
          {name}
        </Link>
      }
      partnerNames={partnerNames}
    />
  );

  const courseCompletionMessage = (
    <FormattedMessage
      message={_t("{userName}'s account is verified. Coursera certifies their successful completion of {productLink}")}
      userName={userName}
      productLink={
        <Link className="product-link" href={productLink}>
          {name}
        </Link>
      }
    />
  );

  return (
    <div className="rc-CompletionOverview">
      <div className="user-course-details">
        <div className="user-photo-container">
          <div className="user-photo-container__icon-background" />
          <SvgCheckSolid
            size={24}
            color="var(--cds-color-white-0)"
            suppressTitle={true}
            style={{
              position: 'absolute',
              right: 0,
              zIndex: 100,
            }}
          />
          {userPhoto ? (
            <img className="user-photo" src={userPhoto} alt={_t('User photo')} />
          ) : (
            <SvgUserFilledStack
              size={Math.round(DEFAULT_AVATAR_SIZE * DEFAULT_AVATAR_RATIO)}
              color={color.white}
              suppressTitle={true}
              stackColor="var(--cds-color-grey-100)"
              stackToIconRatio={DEFAULT_AVATAR_RATIO}
            />
          )}
        </div>
        <div className="course-details">
          <Typography variant="h2semibold" component="h3">
            <FormattedMessage
              message={_t(`Completed by {userName}`)}
              userName={<strong>{userName}</strong>}
              tagName="span"
            />
          </Typography>
          <p>
            <strong>{completionDate}</strong>
          </p>
          {isS12n && totalContentWeeks && (
            <FormattedMessage
              message={_t('Approximately {totalMonths} months at {totalHours} hours a week to complete')}
              totalMonths={totalLearningMonths}
              totalHours={totalLearningHoursPerWeek}
            />
          )}
          {!isS12n && (
            <p>
              <strong>
                {totalDuration && (
                  <FormattedMessage
                    className="m-r-1s"
                    message={_t('{totalHours} hours (approximately)')}
                    totalHours={Math.floor(moment.duration(totalDuration).asHours())}
                  />
                )}
              </strong>
            </p>
          )}
          <p>{includeGrade && grade && <strong>{_t('Grade Achieved: #{grade}%', { grade })}</strong>}</p>
          <p className="account-verification-description">
            {!isS12n ? courseCompletionMessage : s12nCompletionMessage}
          </p>
          {completedCourseNames && (
            <div className="s12n-completed-courses">
              <p className="body-2-text">{_t('Course Certificates Completed')}</p>
              {completedCourseNames.map((courseName) => (
                <p key={courseName}>{courseName}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompletionOverview;
